<script setup>

import { computed, onMounted, ref, watch } from 'vue';
import { buildDataTable, buildItemForIncidentEvent } from '@/composables/incidents/incidentListRenderer';
import { useIncidentStore } from '@/store/incident';
import WYSIWYG from '@/components/WYSIWYG.vue';
import { useRouter } from 'vue-router';
import Incident from '@/components/incidents/IncidentDisplay.vue';
import { useDisplay, useTheme } from 'vuetify';
import { useMiscellaneousStore } from '@/store/miscellaneous';

const props = defineProps({
  incidents: { type: Array, default: () => [] },
  predefinedViewMode: { type: String, required: false, default: null },
  listTitle : { type: String, required: false, default: null },
})

defineEmits(['reloadIncident'])

const incidentStore = useIncidentStore()
const miscellaneousStore = useMiscellaneousStore()
const { mobile } = useDisplay()
const router = useRouter()
const theme = useTheme()

const headers = ref([])
const incidentItems = ref([])
const eventItems = ref({})
const incidentEvents = ref({})
const expandedItems = ref([])
const eventsLoading = ref(false)

const tableItems = computed(() =>  {
  const items = [...incidentItems.value]
  for (let expandedItem of expandedItems.value) {
    const index = items.findIndex(item => item.id === expandedItem)
    if(index === -1 || !eventItems.value[expandedItem]) continue
    items.splice(index + 1, 0, ...eventItems.value[expandedItem])
  }
  return items;
})

function goToIncident(incidentId) {
  const routeData = router.resolve({ name: 'IncidentView', params: { incidentId: incidentId } });
  window.open(routeData.href, '_blank');
}

function setupTable() {
  const dataTableSetup = buildDataTable(props.incidents)
  headers.value = dataTableSetup.headers
  incidentItems.value = dataTableSetup.items
}

async function toggleExpansion(itemId) {
  if(expandedItems.value.includes(itemId)) {

    const index = expandedItems.value.indexOf(itemId)
    expandedItems.value.splice(index, 1)
  } else {
    eventsLoading.value = true;

    if(!Object.keys(eventItems.value).includes(itemId)) {
      incidentEvents.value[itemId] = await incidentStore.loadEventsForIncident(itemId)
      const incident = props.incidents.find(incident => incident.incidentId === itemId)
      eventItems.value[itemId] = incidentEvents.value[itemId].map(incidentEvent => 
        buildItemForIncidentEvent(incidentEvent, incident),
      )
    }
    expandedItems.value.push(itemId)
    eventsLoading.value = false;

  }
}

watch(() => props.incidents, () => {setupTable()})
const viewMode = ref('list')

watch(() => viewMode.value, () => {
  miscellaneousStore.incidentListDisplayMode = viewMode.value
})

onMounted(() => {
  viewMode.value = props.predefinedViewMode ? props.predefinedViewMode : miscellaneousStore.incidentListDisplayMode
  setupTable()
})
</script>

<template>
  <div
    class="mt-8 mb-4 d-flex flex-row align-center flex-wrap"
    :class="
      { 
        'justify-end': !props.listTitle && !mobile,
        'justify-space-between': props.listTitle && !mobile
      }
    "
  >
    <v-card-title
      v-if="props.listTitle"
      style="max-width: 100%"
    >
      {{ props.listTitle }}
    </v-card-title>

    <v-btn-toggle
      v-if="!mobile"
      v-model="viewMode"
      class="align-self-end"
      :mandatory="true"
      density="comfortable"
      divided
      elevation="2"
    >
      <v-btn
        class="rounded-0"
        value="list"
        color="primary"
        height="40"
        :base-color="theme.current.value.colors.surface"
      >
        <v-icon
          icon="mdi-view-list"
          size="24"
        />
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ $t("general_interface.buttons.list") }}
        </v-tooltip>
      </v-btn>
      <v-btn
        class="rounded-0"
        value="table"
        color="primary"
        height="40"
        :base-color="theme.current.value.colors.surface"
      >
        <v-icon
          icon="mdi-table"
          size="24"
        />
        <v-tooltip
          activator="parent"
          location="top"
        >
          {{ $t("general_interface.buttons.table") }}
        </v-tooltip>
      </v-btn>
    </v-btn-toggle>
  </div>

  <v-divider class="mb-4" />

  <v-progress-linear
    v-if="eventsLoading"
    :indeterminate="true"
  />

  <v-table
    v-if="viewMode === 'table' && !mobile"
    class="rounded-t-lg"
    :fixed-header="true"
  >
    <thead>
      <tr>
        <th class="text-center">
          {{ $t("incidentList.headers.expand") }}
        </th>
        <th
          v-for="header in headers"
          :key="header.id"
          class="text-center"
        >
          {{ header.title }}
        </th>
        <th><!-- for "more" button, doesn't need a header text --></th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="item in tableItems"
        :key="item.id"
        class="rounded"
      >
        <td
          :style="`border: 1px solid ${item.isIncident ? 'black' : 'grey'}`"
        >
          <v-btn
            v-if="item.isIncident === true"
            :icon="expandedItems.includes(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            variant="tonal"
            color="primary"
            size="small"
            @click="toggleExpansion(item.id)"
          />
        </td>
        <td
          v-for="header in headers"
          :key="`${item.id}_${header.id}`"
          :class="header.wysiwyg ? undefined : 'text-center'"
          :style="`border: 1px solid ${item.isIncident ? 'black' : 'grey'}`"
        >
          <WYSIWYG
            v-if="header.wysiwyg"
            :model-value="item[header.key]"
            :read-only="true"
          />
          <span v-if="!header.wysiwyg">{{ item[header.key] }}</span>
        </td>
        <td :style="`border: 1px solid ${item.isIncident ? 'black' : 'grey'}`">
          <v-btn
            v-if="item.isIncident === true"
            size="small"
            variant="flat"
            color="primary"
            @click="goToIncident(item.id)"
          >
            {{ $t("general_interface.buttons.more") }}
          </v-btn>
        </td>
      </tr>
    </tbody>
  </v-table>

  <v-row v-if="viewMode === 'list' || mobile">
    <v-col
      v-for="incident of incidents"
      :key="incident.incidentId"
      :cols="12"
    >
      <Incident
        :incident="incident"
        @reload="$emit('reloadIncident', incident.incidentId)"
      />
    </v-col>
  </v-row>
</template>

<style scoped>

</style>
